import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Guest session store. Holds two pieces of identity:
//   1. guestSessionId  — random UUID generated client-side, used as the
//      ownership marker on OrgContractDocument and as the
//      X-Guest-Session-Id header value on all backend calls.
//   2. guestBlocksToken — UDS Blocks token from the backend's
//      /api/v1/guest/blocks/access-token endpoint. Used for direct UDS
//      reads/writes against OrgContractDocument. The token's role is
//      configured on UDS to read+write OrgContractDocument and have NO
//      access to GuestPayment, which is what makes it safe to expose.
//
// IMPORTANT: ensureGuestSession() must be called LAZILY — only when the
// user enters the generate flow. Calling it eagerly on every page load
// would mint a UUID for every visitor including those who never engage,
// flooding the document collection with empty rows.

const TOKEN_REFRESH_BUFFER_MS = 60 * 1000;
const BACKEND_URL =
  (import.meta.env.VITE_BACKEND_API_URL as string | undefined)?.replace(/\/$/, '') || '';

interface GuestSessionState {
  guestSessionId: string | null;
  guestBlocksToken: string | null;
  guestBlocksTokenExpiresAt: number | null;
  ensureGuestSession: () => Promise<void>;
  getGuestBlocksToken: () => Promise<string>;
  clear: () => void;
}

const generateGuestSessionId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for older browsers — not cryptographically perfect but enough
  // entropy for a guest session bearer secret.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const fetchGuestBlocksToken = async (): Promise<{
  accessToken: string;
  expiresAt: number;
}> => {
  if (!BACKEND_URL) {
    throw new Error('VITE_BACKEND_API_URL is not configured; cannot fetch guest Blocks token');
  }
  const res = await fetch(`${BACKEND_URL}/guest/blocks/access-token`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) {
    throw new Error(`Guest Blocks token fetch failed: ${res.status} ${res.statusText}`);
  }
  const payload = (await res.json()) as {
    success?: boolean;
    data?: { accessToken: string; expiresAt: number };
  };
  if (!payload?.data?.accessToken || !payload.data.expiresAt) {
    throw new Error('Guest Blocks token response missing fields');
  }
  return {
    accessToken: payload.data.accessToken,
    expiresAt: payload.data.expiresAt,
  };
};

export const useGuestSessionStore = create<GuestSessionState>()(
  persist(
    (set, get) => ({
      guestSessionId: null,
      guestBlocksToken: null,
      guestBlocksTokenExpiresAt: null,

      async ensureGuestSession() {
        const state = get();
        if (!state.guestSessionId) {
          set({ guestSessionId: generateGuestSessionId() });
        }
        const expiresAt = get().guestBlocksTokenExpiresAt;
        const needsToken =
          !get().guestBlocksToken ||
          !expiresAt ||
          Date.now() + TOKEN_REFRESH_BUFFER_MS >= expiresAt;
        if (needsToken) {
          const { accessToken, expiresAt: newExpiresAt } = await fetchGuestBlocksToken();
          set({
            guestBlocksToken: accessToken,
            guestBlocksTokenExpiresAt: newExpiresAt,
          });
        }
      },

      async getGuestBlocksToken() {
        await get().ensureGuestSession();
        const token = get().guestBlocksToken;
        if (!token) {
          throw new Error('Failed to acquire guest Blocks token');
        }
        return token;
      },

      clear() {
        set({
          guestSessionId: null,
          guestBlocksToken: null,
          guestBlocksTokenExpiresAt: null,
        });
      },
    }),
    {
      name: 'guest-session-storage',
    }
  )
);

export const getGuestSessionId = (): string | null =>
  useGuestSessionStore.getState().guestSessionId;

export const getGuestBlocksTokenSync = (): string | null =>
  useGuestSessionStore.getState().guestBlocksToken;
